import { useState, useRef, useCallback } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Cloud, Upload, Check, X, Loader2 } from 'lucide-react';
import { useUploadMutation } from '../hooks';
import { validateFiles } from '../utils';
import { useUploadContext } from './upload-provider';

type UploadStatus = 'idle' | 'uploading' | 'success' | 'error';

interface UploadButtonProps {
  onUploadSuccess?: (succeeded: string[], failed: string[]) => void;
  onUploadError?: (error: Error) => void;
}

export function UploadButton({ onUploadSuccess, onUploadError }: UploadButtonProps) {
  const { meta, actions } = useUploadContext();
  const { config } = meta;
  const { docType, extensionFile } = config;

  const [status, setStatus] = useState<UploadStatus>('idle');
  const [errorDialog, setErrorDialog] = useState<{
    open: boolean;
    title: string;
    messages: string[];
  }>({
    open: false,
    title: '',
    messages: [],
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  const { upload, isUploading } = useUploadMutation({
    docType,
    onSuccess: (result) => {
      if (result.failed.length > 0) {
        setStatus('error');
        setErrorDialog({
          open: true,
          title: 'Upload parcialmente concluído',
          messages: result.failed.map((name) => `${name}: Falha no envio`),
        });
      } else {
        setStatus('success');
        setTimeout(() => setStatus('idle'), 3000);
      }
      actions.invalidate();
      onUploadSuccess?.(result.succeeded, result.failed);
    },
    onError: (error) => {
      setStatus('error');
      setErrorDialog({
        open: true,
        title: 'Erro no upload',
        messages: [error.message],
      });
      onUploadError?.(error);
    },
  });

  const handleButtonClick = useCallback(() => {
    setStatus('idle');
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files || []);
      if (files.length === 0) {
        return;
      }

      const { validFiles, errors } = validateFiles(files, extensionFile);

      if (errors.length > 0) {
        setStatus('error');
        setErrorDialog({
          open: true,
          title: 'Arquivos inválidos',
          messages: errors,
        });

        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      setStatus('uploading');
      upload(validFiles);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [extensionFile, upload]
  );

  const handleCloseDialog = useCallback(() => {
    setErrorDialog((prev) => ({ ...prev, open: false }));
  }, []);

  const getButtonIcon = () => {
    if (isUploading) {
      return <Loader2 className="h-4 w-4 animate-spin" />;
    }
    if (status === 'success') {
      return <Check className="h-4 w-4" />;
    }
    if (status === 'error') {
      return <X className="h-4 w-4" />;
    }
    return <Upload className="h-4 w-4" />;
  };

  const getButtonVariant = () => {
    if (status === 'success') {
      return 'default';
    }
    if (status === 'error') {
      return 'destructive';
    }
    return 'secondary';
  };

  return (
    <>
      <Card className="min-h-[140px]">
        <CardContent className="p-6 flex flex-col justify-between h-full">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="mb-2 text-foreground">
                <Cloud className="h-6 w-6" />
              </div>
              <p className="text-sm font-medium text-muted-foreground mb-2">
                Arquivos {extensionFile}
              </p>
              <p className="text-lg font-semibold text-foreground">Upload</p>
            </div>
            <div className="ml-4 flex-shrink-0">
              <Button
                variant={getButtonVariant()}
                size="icon"
                className="h-12 w-12 rounded-full"
                onClick={handleButtonClick}
                disabled={isUploading}
              >
                {getButtonIcon()}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept={extensionFile}
                multiple
                className="hidden"
                onChange={handleFileChange}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={errorDialog.open} onOpenChange={(open) => !open && handleCloseDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{errorDialog.title}</DialogTitle>
          </DialogHeader>
          <div className="max-h-60 overflow-y-auto">
            <ul className="space-y-2">
              {errorDialog.messages.map((message, index) => (
                <li key={index} className="text-sm text-destructive">
                  {message}
                </li>
              ))}
            </ul>
          </div>
          <DialogFooter>
            <Button onClick={handleCloseDialog}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
